// src/components/PdfRenderer.js
import React, { useState, useRef, useCallback, forwardRef } from 'react';
import { Document, Page } from 'react-pdf';
import './PdfRenderer.css';

// Wrap the component with forwardRef to accept a ref from the parent
const PdfRenderer = forwardRef(({ pdfFile, rectangles, onAddRectangle, onUpdateRectangle, deleteRectangle, pageRef }, ref) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  
  const [mode, setMode] = useState('none');
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [currentRect, setCurrentRect] = useState(null);
  const [interactionInfo, setInteractionInfo] = useState(null);

  const onDocumentLoadSuccess = useCallback((pdf) => setNumPages(pdf.numPages), []);

  const getCoords = (e) => {
    if (!ref || !ref.current) return { x: 0, y: 0 };
    const rect = ref.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handleMouseDown = (e) => {
    const target = e.target;
    const coords = getCoords(e);
    setStartPos(coords);

    if (target.classList.contains('resizer')) {
      const boxId = target.dataset.id;
      const direction = target.dataset.direction;
      const originalRect = rectangles.find(r => r.uniqueId === boxId);
      setMode('resizing');
      setInteractionInfo({ id: boxId, direction, originalRect });
    } else if (target.classList.contains('drawn-box') || target.classList.contains('default-value-textarea')) {
      const boxId = target.dataset.id || target.parentElement.dataset.id;
      const rect = rectangles.find(r => r.uniqueId === boxId);
      if (rect) {
        const offsetX = coords.x - rect.x;
        const offsetY = coords.y - rect.y;
        setMode('dragging');
        setInteractionInfo({ id: boxId, offsetX, offsetY });
      }
    } else {
      setMode('drawing');
      setCurrentRect({ x: coords.x, y: coords.y, width: 0, height: 0, page: pageNumber });
    }
  };

  const handleMouseMove = (e) => {
    if (mode === 'none' || !ref || !ref.current) return;
    
    const coords = getCoords(e);
    const dx = coords.x - startPos.x;
    const dy = coords.y - startPos.y;

    switch (mode) {
      case 'drawing':
        setCurrentRect(prev => ({ ...prev, width: dx, height: dy }));
        break;
      
      case 'dragging':
        if (interactionInfo) {
          onUpdateRectangle(interactionInfo.id, { x: coords.x - interactionInfo.offsetX, y: coords.y - interactionInfo.offsetY });
        }
        break;

      case 'resizing':
        if (interactionInfo) {
          const { id, direction, originalRect } = interactionInfo;
          let { x, y, width, height } = originalRect;
          
          if (direction.includes('right')) width = coords.x - x;
          if (direction.includes('bottom')) height = coords.y - y;
          if (direction.includes('left')) { width = (x + width) - coords.x; x = coords.x; }
          if (direction.includes('top')) { height = (y + height) - coords.y; y = coords.y; }
          
          if (width > 5 && height > 5) {
            onUpdateRectangle(id, { x, y, width, height });
          }
        }
        break;
      
      default:
        break;
    }
  };

  const handleMouseUp = () => {
    if (mode === 'drawing' && currentRect) {
      const normalizedRect = {
        x: currentRect.width < 0 ? currentRect.x + currentRect.width : currentRect.x,
        y: currentRect.height < 0 ? currentRect.y + currentRect.height : currentRect.y,
        width: Math.abs(currentRect.width),
        height: Math.abs(currentRect.height),
      };
      if (normalizedRect.width > 5 && normalizedRect.height > 5) {
        onAddRectangle({
          page: pageNumber,
          ...normalizedRect,
        });
      }
    }
    setMode('none');
    setCurrentRect(null);
    setInteractionInfo(null);
  };

  const handleBoxDelete = (e, uniqueId) => {
    e.stopPropagation();
    deleteRectangle(uniqueId);
  };

  const changePage = (offset) => setPageNumber(prev => Math.max(1, Math.min(prev + offset, numPages || 1)));
  const previousPage = () => changePage(-1);
  const nextPage = () => changePage(1);

  const pageRects = rectangles.filter(rect => rect.page === pageNumber);

  return (
    <div className="pdf-renderer-container">
      <div 
        className="pdf-viewer" 
        ref={ref} // Use the forwarded ref here
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <Document file={pdfFile} onLoadSuccess={onDocumentLoadSuccess} className="pdf-document">
          <Page 
            pageNumber={pageNumber} 
            className="pdf-page"
            renderAnnotationLayer={false}
            renderTextLayer={false}
            inputRef={pageRef}
          />
        </Document>
        {pageRects.map((rect) => (
          <div
            key={rect.uniqueId}
            data-id={rect.uniqueId}
            className="drawn-box"
            style={{
              left: `${rect.x}px`,
              top: `${rect.y}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              cursor: 'grab',
            }}
            title={`ID: ${rect.id}`}
          >
            {rect.type === 'text' && (
              <textarea
                className="default-value-textarea"
                value={rect.defaultValue || ''}
                onChange={(e) => onUpdateRectangle(rect.uniqueId, { defaultValue: e.target.value })}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder="Default Text..."
              />
            )}
            <button className="delete-box-btn" onClick={(e) => handleBoxDelete(e, rect.uniqueId)}>&times;</button>
            {['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top', 'bottom', 'left', 'right'].map(dir => (
              <div key={dir} data-id={rect.uniqueId} data-direction={dir} className={`resizer ${dir}`} />
            ))}
          </div>
        ))}
        {mode === 'drawing' && currentRect && (
          <div
            className="drawing-box"
            style={{
              left: `${currentRect.width < 0 ? currentRect.x + currentRect.width : currentRect.x}px`,
              top: `${currentRect.height < 0 ? currentRect.y + currentRect.height : currentRect.y}px`,
              width: `${Math.abs(currentRect.width)}px`,
              height: `${Math.abs(currentRect.height)}px`,
            }}
          />
        )}
      </div>
      <div className="pagination-controls">
        <p>Page {pageNumber || '--'} of {numPages || '--'}</p>
        <button type="button" disabled={pageNumber <= 1} onClick={previousPage}>Previous</button>
        <button type="button" disabled={pageNumber >= numPages} onClick={nextPage}>Next</button>
      </div>
    </div>
  );
});

export default PdfRenderer;